import { useMemo } from "react";
import { AlertCircle, Pause, Play, Square, Volume2 } from "lucide-react";
import type { StudioController } from "../useStudio";
import { rehearsalTextFrom } from "../studio/rehearsalText";
import "./rehearsal-controls.css";

/** A second control surface for the existing rehearsal session, never a second voice. */
export function RehearsalControls({ studio }: {
  studio: Pick<StudioController, "parsed" | "state" | "rehearsal">;
}) {
  const rehearsal = studio.rehearsal;
  const text = useMemo(
    () => rehearsalTextFrom(studio.parsed.words, studio.state.wordIndex),
    [studio.parsed.words, studio.state.wordIndex],
  );
  const resumable = rehearsal.speaking && rehearsal.paused;
  const unavailable = !rehearsal.supported;
  const playHelp = unavailable
    ? "This browser does not support reading aloud."
    : resumable
      ? "Resume the paused rehearsal from the same spoken position."
      : !text
        ? "There are no readable words remaining at this position."
        : rehearsal.speaking
          ? "The script is being read aloud. Pause or stop this rehearsal first."
          : "Read aloud from the selected word in Talent view. Stage cues are skipped. Uses the voice, rate and pitch in Studio controls → Rehearsal, and stops microphone listening.";
  const state = unavailable ? "unavailable" : rehearsal.error ? "error"
    : resumable ? "paused" : rehearsal.speaking ? "speaking" : "idle";
  return (
    <div className="quick-rehearsal" role="group" aria-label="Quick rehearsal" data-state={state}>
      <span className="quick-rehearsal-label" data-help="Read the script aloud through your speakers using the rehearsal voice. These controls share the Rehearsal session in Studio controls." tabIndex={0}>
        <Volume2 size={14} aria-hidden="true" />
        <span className="visually-hidden">Read aloud</span>
      </span>
      <button
        type="button"
        className="quick-rehearsal-button quick-rehearsal-play"
        aria-label={resumable ? "Resume reading aloud" : "Read aloud from here"}
        data-help={playHelp}
        disabled={unavailable || (!resumable && (!text || rehearsal.speaking))}
        onClick={() => resumable ? rehearsal.resume() : rehearsal.speak(text)}
      ><Play size={14} aria-hidden="true" /></button>
      <button
        type="button"
        className="quick-rehearsal-button"
        aria-label="Pause reading aloud"
        data-help={resumable ? "Reading aloud is paused. Use Play to resume." : "Pause reading aloud and keep the spoken position for resuming."}
        disabled={unavailable || !rehearsal.speaking || rehearsal.paused}
        onClick={rehearsal.pause}
      ><Pause size={14} aria-hidden="true" /></button>
      <button
        type="button"
        className="quick-rehearsal-button"
        aria-label="Stop reading aloud"
        data-help="Stop reading aloud. The next Play starts from the selected word in Talent view."
        disabled={unavailable || !rehearsal.speaking}
        onClick={rehearsal.stop}
      ><Square size={13} aria-hidden="true" /></button>
      <span className="visually-hidden" role="status">
        {unavailable ? "Reading aloud is unavailable in this browser."
          : resumable ? "Reading aloud paused." : rehearsal.speaking ? "Reading aloud." : "Reading aloud stopped."}
      </span>
      {rehearsal.error && (
        <span className="quick-rehearsal-error" role="alert" data-help={rehearsal.error} tabIndex={0}>
          <AlertCircle size={14} aria-hidden="true" />
          <span>Voice unavailable</span>
          <span className="visually-hidden">: {rehearsal.error}</span>
        </span>
      )}
    </div>
  );
}
